import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { notifySuccess, notifyError } from "../../utils/ToastifyProvider";
import { useLoader } from "../../utils/LoaderContext"; // Adjust path as needed
import { useNavigate } from "react-router";

// const BACKEND_URL = process.env.REACT_APP_API_BASE_URL;
// const BACKEND_URL = "http://localhost:4000/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ✅ Hook to fetch all products with global loader
export const useAllProducts = () => {
  const { setLoading } = useLoader();

  return useQuery({
    queryKey: ["all-products"],
    queryFn: async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/products`);
        return data.products;
      } catch (error) {
        notifyError(error.response?.data?.message || "Failed to load products");
        throw error;
      } finally {
        setLoading(false);
      }
    },
    staleTime: 5 * 60 * 1000,
  });
};

// ✅ Hook to create product with global loader
export const useCreateProduct = () => {
  const queryClient = useQueryClient();
  const { setLoading } = useLoader();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (productData) => {
      const token = localStorage.getItem("token");
      setLoading(true);
      try {
        const { data } = await axios.post(
          `${BACKEND_URL}/add-product`,
          productData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        return data;
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
    onSuccess: (data) => {
      notifySuccess(data.message || "Product created successfully");
      queryClient.invalidateQueries(["all-products"]);
      navigate("/products");
    },
    onError: (error) => {
      notifyError(error.response?.data?.message || "Failed to create product");
    },
  });
};

export const useUpdateProduct = () => {
  const queryClient = useQueryClient();
  const { setLoading } = useLoader();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ id, updatedData }) => {
      const token = localStorage.getItem("token");
      setLoading(true);
      try {
        const { data } = await axios.patch(
          `${BACKEND_URL}/update-product/${id}`,
          updatedData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        return data;
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
      }
    },
    onSuccess: (data) => {
      notifySuccess(data.message || "Product updated successfully");
      queryClient.invalidateQueries(["all-products"]);
      navigate("/products");
    },
    onError: (error) => {
      notifyError(error.response?.data?.message || "Failed to update product");
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const token = localStorage.getItem("token"); // Or your method of retrieving token

      const { data } = await axios.delete(
        `${BACKEND_URL}/delete-product/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      return data;
    },
    onSuccess: () => {
      notifySuccess("Product deleted successfully.");
      queryClient.invalidateQueries(["all-products"]);
    },
    onError: (error) => {
      notifyError(error.response?.data?.message || "Failed to delete product.");
    },
  });
};

export const useSearchProducts = (searchText) => {
  return useQuery({
    queryKey: ["search-products", searchText],
    queryFn: async () => {
      if (!searchText) return [];

      const token = localStorage.getItem("token");
      const { data } = await axios.get(
        `${BACKEND_URL}/search-products?name=${searchText}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return data;
    },
    enabled: !!searchText, // only run when searchText is not empty
    staleTime: 1000 * 60 * 5, // optional
    onError: (error) => {
      notifyError(
        error.response?.data?.message || "Failed to search products."
      );
    },
  });
};
